import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { linksAPI } from '@/lib/api';
import OBCLanDiscovery from '@/lib/obcLanDiscovery';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Maximize2, Minimize2, Link2, Wifi, Globe, RefreshCw } from 'lucide-react';

export default function LinkViewerPage() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [connectionMode, setConnectionMode] = useState('cloud'); // 'cloud' | 'lan' | 'probing'
  const [iframeSrc, setIframeSrc] = useState('');
  const [lanInfo, setLanInfo] = useState(null); // { localUrl, method }
  const iframeRef = useRef(null);

  useEffect(() => {
    loadLink();
  }, [linkId]);

  const loadLink = async () => {
    try {
      const res = await linksAPI.getAll();
      const found = res.data.find(l => l.link_id === linkId);
      if (found) {
        setLink(found);
        // Probe LAN
        setConnectionMode('probing');
        const result = await OBCLanDiscovery.checkOne(found);
        if (result.reachable && result.localUrl) {
          setLanInfo(result);
          setConnectionMode('lan');
          const fullLocalUrl = OBCLanDiscovery.buildLocalUrl(result.localUrl, found.url);
          setIframeSrc(fullLocalUrl);
        } else {
          setLanInfo(null);
          setConnectionMode('cloud');
          setIframeSrc(found.url);
        }
      } else {
        toast.error('OBC non trovato');
        navigate(-1);
      }
    } catch {
      toast.error("Errore nel caricamento dell'OBC");
    } finally {
      setLoading(false);
    }
  };

  const switchToCloud = useCallback(() => {
    if (!link) return;
    setConnectionMode('cloud');
    setIframeSrc(link.url);
    toast.info('Connessione via Cloud');
  }, [link]);

  const switchToLan = useCallback(async () => {
    if (!link) return;
    setConnectionMode('probing');
    const result = await OBCLanDiscovery.checkOne(link);
    if (result.reachable && result.localUrl) {
      setLanInfo(result);
      setConnectionMode('lan');
      const fullLocalUrl = OBCLanDiscovery.buildLocalUrl(result.localUrl, link.url);
      setIframeSrc(fullLocalUrl);
      toast.success('Connessione LAN attiva');
    } else {
      setLanInfo(null);
      setConnectionMode('cloud');
      setIframeSrc(link.url);
      toast.error('OBC non raggiungibile in LAN. Accetta il certificato prima.');
    }
  }, [link]);

  const openCertAccept = useCallback(() => {
    if (!link) return;
    const obcName = OBCLanDiscovery.extractOBCName(link.url);
    if (obcName) {
      window.open(`https://${obcName}.local/`, '_blank');
      toast.info('Accetta il certificato nel browser, poi clicca "Connetti LAN"');
    }
  }, [link]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!link) return null;

  const obcName = OBCLanDiscovery.extractOBCName(link.url);

  return (
    <div className={`space-y-4 ${fullscreen ? 'fixed inset-0 z-50 bg-background p-4' : ''}`}>
      <div className="relative w-full rounded-t-lg border border-border overflow-hidden bg-card" data-testid="link-viewer-container">
        <div className="h-10 bg-muted/50 flex items-center px-4 justify-between border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              <button
                onClick={() => navigate(-1)}
                className="w-3 h-3 rounded-full bg-red-500 hover:bg-red-400 transition-colors cursor-pointer"
                data-testid="link-viewer-close"
              />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <button
                onClick={() => setFullscreen(!fullscreen)}
                className="w-3 h-3 rounded-full bg-green-500 hover:bg-green-400 transition-colors cursor-pointer"
                data-testid="link-viewer-fullscreen"
              />
            </div>
            <div className="flex items-center gap-2 ml-2">
              <Link2 className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground" data-testid="link-viewer-name">
                {link.name}
              </span>
              {connectionMode === 'lan' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-500/10 text-green-500 border border-green-500/20" data-testid="connection-lan-badge">
                  <Wifi className="w-3 h-3" /> LAN
                  {lanInfo?.method === 'mdns' && <span className="opacity-60">(.local)</span>}
                </span>
              )}
              {connectionMode === 'cloud' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-500/10 text-blue-500 border border-blue-500/20" data-testid="connection-cloud-badge">
                  <Globe className="w-3 h-3" /> Cloud
                </span>
              )}
              {connectionMode === 'probing' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-yellow-500/10 text-yellow-500 border border-yellow-500/20">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Verifica LAN...
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {obcName && (
              <>
                {connectionMode !== 'lan' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={switchToLan}
                    data-testid="switch-to-lan-btn"
                  >
                    <Wifi className="w-3 h-3" /> Connetti LAN
                  </Button>
                )}
                {connectionMode === 'lan' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={switchToCloud}
                    data-testid="switch-to-cloud-btn"
                  >
                    <Globe className="w-3 h-3" /> Usa Cloud
                  </Button>
                )}
                {connectionMode === 'cloud' && !lanInfo?.reachable && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1 text-muted-foreground"
                    onClick={openCertAccept}
                    title="Apri in nuova scheda per accettare il certificato HTTPS"
                    data-testid="accept-cert-btn"
                  >
                    <Wifi className="w-3 h-3" /> Accetta Cert
                  </Button>
                )}
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setFullscreen(!fullscreen)}
              data-testid="link-viewer-toggle-fullscreen"
            >
              {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => navigate(-1)}
              data-testid="link-viewer-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          title={link.name}
          data-testid="link-viewer-iframe"
          className="w-full bg-white block"
          style={{ height: fullscreen ? 'calc(100vh - 6rem)' : '85vh' }}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </div>
    </div>
  );
}
